import { padStart } from 'lodash';
import { FieldTypes } from 'store/assets/types';
import {
  FieldField,
  FieldGroup,
  FieldGroupInfo,
  FieldGroupWithCalculatedInfo,
} from 'store/organisations/types';
import { FieldInstanceWithFormattedAutoId, FieldResult } from 'types/types';
import { rbAddReferencePrefix } from './riskbase';

export const hasValue = (result: FieldResult | undefined | null): boolean =>
  !!result?.value?.length || !!result?.pin;

export const returnFieldInstanceIdsMatchingSearchTerm = ({
  group,
  fields,
  instances,
  searchTerms,
}: {
  group: Omit<FieldGroupWithCalculatedInfo, 'fields' | 'checks'>;
  fields: Record<
    string,
    Pick<FieldGroupWithCalculatedInfo['fields'][string], 'options'>
  >;
  instances: Record<string, FieldInstanceWithFormattedAutoId>;
  searchTerms: string[];
}): Record<string, FieldInstanceWithFormattedAutoId> => {
  if (!group || !searchTerms.length) {
    return instances;
  }
  if (
    !group.calculatedInfo.stringFieldIds.length &&
    !group.calculatedInfo.optionFieldIds.length
  ) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(instances).filter(([, instance]) => {
      const searchString = [
        instance.reference ? rbAddReferencePrefix(instance.reference) : '',
        instance.formattedAutoId || '',
        group.calculatedInfo.stringFieldIds
          .reduce<string[]>((acc1, fieldId) => {
            const v = instance.results[fieldId]?.value as string;
            if (v) {
              acc1.push(v);
            }
            return acc1;
          }, [])
          .join(' '),
        group.calculatedInfo.optionFieldIds
          .reduce<string[]>((acc1, fieldId) => {
            const v = getOptionFieldResultDisplayValues(
              fields[fieldId]!,
              instance.results[fieldId],
            );
            if (v.length) {
              acc1.push(v.join(' '));
            }
            return acc1;
          }, [])
          .join(' '),
      ]
        .join(' ')
        .toLowerCase();
      if (
        searchString &&
        searchTerms.every((term) => searchString.includes(term))
      ) {
        return true;
      }
      return false;
    }),
  );
};

export const getOptionFieldResultDisplayValues = (
  field: Pick<FieldField, 'options'>,
  result: FieldResult | undefined,
  ignoreYesNo: boolean = false,
): string[] => {
  const value = result?.value;
  const displayValues: string[] = [];
  if (Array.isArray(value) && value.length) {
    field.options.forEach((option) => {
      if (!ignoreYesNo || (option.name !== 'Yes' && option.name !== 'No')) {
        if (value.includes(option.id)) {
          displayValues.push(option.name);
        }
      }
    });
  }
  return displayValues;
};

export const calculateAutoId = (
  autoId: number | undefined,
  draftAutoId: number | undefined,
  autoIdNext: number,
): number => autoId || (draftAutoId || 1) - 1 + autoIdNext;

export const displayAutoId = (
  id: number,
  zeroFill: number | undefined,
  prefix: string | undefined,
): string => `${prefix || ''}${padStart(`${id}`, zeroFill || 0, '0')}`;

export const getFieldGroupInfo = (
  group: Omit<FieldGroup, 'fields'>,
  fields: Record<string, FieldField>,
): FieldGroupInfo => {
  const info: FieldGroupInfo = {
    singularName: group.nameSingular || group.name || 'item',
    stringFieldIds: [],
    textFieldIds: [],
    textareaFieldIds: [],
    numberFieldIds: [],
    optionFieldIds: [],
    imageFieldId: undefined,
    pinFieldId: undefined,
    qrFieldId: undefined,
  };

  for (const fieldId of group.fieldIds) {
    const field = fields[fieldId]!;
    if (field.type === FieldTypes.TEXT) {
      info.stringFieldIds.push(field.id);
      info.textFieldIds.push(field.id);
    } else if (field.type === FieldTypes.NOTE) {
      info.stringFieldIds.push(field.id);
      info.textareaFieldIds.push(field.id);
    } else if (field.type === FieldTypes.OPTION) {
      info.optionFieldIds.push(field.id);
    } else if (field.type === FieldTypes.IMAGE) {
      if (!info.imageFieldId && !field.multiSelect) {
        info.imageFieldId = field.id;
      }
    } else if (field.type === FieldTypes.PIN) {
      if (!info.pinFieldId) {
        info.pinFieldId = field.id;
      }
    } else if (field.type === FieldTypes.QR) {
      if (!info.qrFieldId) {
        info.qrFieldId = field.id;
      }
    }
  }
  return info;
};
